import * as B from 'fp-ts/lib/boolean'
import * as E from 'fp-ts/lib/Either'
import * as F from 'fp-ts/lib/function'
import * as P from 'fp-ts/lib/pipeable'
import * as TE from 'fp-ts/lib/TaskEither'
import { Errors } from 'io-ts'
import { PathReporter } from 'io-ts/lib/PathReporter'
import { useCallback } from 'react'
import { ApiFetchError } from '../../constants/errors/ApiFetchError'
import { ApiResponseError } from '../../constants/errors/ApiResponseError'
import { ResponseError as TApiResponseError, FetchError } from '../../types/error'
import { HttpMethod, HttpStatus } from '../../types/fetch'
import { getApiAuthToken } from '../../utils/getApiAuthToken'
import { withParams } from '../../utils/http/withParams'
import { withQuery } from '../../utils/http/withQuery'
import { useApiErrorHandler } from '../useApiErrorHandler'

type Param<R> = {
  auth?: boolean
  body?: BodyInit | Record<string, any>
  decoder?: (payload: unknown) => E.Either<Errors, R>
  headers?: HeadersInit
  endpoint: string
  params?: Record<string, string | number | boolean>
  query?: Record<string, string | number | boolean>
  method: HttpMethod
}

export type DecodedHttpResponse<R> = {
  headers?: Headers
  payload: R
  status: HttpStatus
}

export const useFetch = () => {
  const apiErrorHandler = useApiErrorHandler()

  return useCallback(
    <R>({
      auth,
      body = {},
      decoder = (payload: unknown) => E.right(F.identity(payload as R)),
      endpoint,
      headers = {},
      method,
      params = {},
      query = {},
    }: Param<R>) =>
      P.pipe(
        !!auth,
        B.fold(
          () => TE.right(''),
          () => getApiAuthToken
        ),
        TE.chainW(token =>
          P.pipe(
            TE.tryCatch(
              async (): Promise<
                E.Either<TApiResponseError | FetchError, DecodedHttpResponse<R> & { type: 'ApiSuccess' }>
              > => {
                const composedEndpoint = P.pipe(endpoint, withParams(params), withQuery(query))

                const response = await window.fetch(composedEndpoint, {
                  headers: new Headers({
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    ...headers,
                    ...(token && { Authorization: `Bearer ${token}` }),
                  }),
                  method,
                  cache: 'no-store' as const,
                  credentials: 'same-origin' as const,
                  ...(body !== undefined &&
                    !['GET', 'HEAD'].includes(method) && {
                      body: typeof body === 'string' ? body : JSON.stringify(body),
                    }),
                })

                if (response.status > 300) {
                  return E.left({
                    error: new ApiResponseError(`Status code: ${response.status}`, response),
                    type: 'ApiResponseError',
                  })
                }

                const text = await response.text()
                const data = text ? JSON.parse(text) : {}
                const decodedPayload = decoder(data)

                if (E.isLeft(decodedPayload)) {
                  return E.left({
                    error: new ApiResponseError(PathReporter.report(decodedPayload).join('\n'), response),
                    type: 'ApiResponseError',
                  })
                }

                return E.right({
                  headers: response.headers,
                  payload: decodedPayload.right,
                  status: response.status as HttpStatus,
                  type: 'ApiSuccess',
                })
              },
              (e): FetchError => ({
                error: new ApiFetchError(`Fetch is failed for some unknown reasons ${e}`),
                type: 'ApiFetchError',
              })
            ),
            TE.chain(e =>
              F.pipe(
                e,
                E.fold(
                  l => async () => {
                    await apiErrorHandler(l)

                    return E.left(l)
                  },
                  r => async () => {
                    return E.right(r)
                  }
                )
              )
            )
          )
        )
      )(),
    [apiErrorHandler]
  )
}

export type FetchResponse = ReturnType<ReturnType<typeof useFetch>> extends Promise<infer U> ? U : never
